import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { scrollToTop } from "@/utils";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  FileText,
  Gavel,
  Globe,
  Mail,
  MapPin,
  Scale,
  ShieldAlert,
} from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const PageTermsOfService = () => {
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col justify-between">
      <Helmet>
        <title>Conditions Générales d'Utilisation | Église de Dieu Garizim</title>
        <meta
          name="description"
          content="Conditions Générales d'Utilisation régissant la consultation et l'utilisation de la plateforme egarizim.com (articles, cantiques, services chrétiens)."
        />
        <link rel="canonical" href="https://egarizim.com/conditions-utilisation" />
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Header */}
        <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-12 sm:py-16 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-amber-300 text-xs font-bold uppercase tracking-wider mb-4 backdrop-blur-sm">
              <Scale className="w-4 h-4" />
              <span>Cadre Juridique & Utilisation</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-4">
              Conditions d'Utilisation
            </h1>
            <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              Dernière révision : 1er Février 2026. Modalités et règles
              encadrant l'accès au site officiel de l'Église de Dieu Garizim.
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="container mx-auto max-w-4xl px-4 sm:px-6 -mt-6">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-10 space-y-10 text-slate-700 leading-relaxed text-sm sm:text-base">
            {/* 1. Préambule */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <FileText className="w-5 h-5 text-church-blue-600" />
                1. Préambule et Acceptation des Conditions
              </h2>
              <p>
                Le présent site internet accessible à l'adresse{" "}
                <strong>https://egarizim.com</strong> (ci-après le « Site ») est
                édité par l'<strong>Église de Dieu de Garizim</strong>, organisme
                religieux légalement constitué dans la province de Québec
                (Canada), dont le siège principal est situé au 11800 4e Avenue,
                Montréal, QC H1E 5Y2.
              </p>
              <p>
                L'accès, la navigation et l'utilisation du Site impliquent
                l'acceptation pleine, entière et sans réserve des présentes
                Conditions Générales d'Utilisation (CGU). Si vous n'acceptez pas
                ces conditions dans leur intégralité, vous êtes prié de cesser
                immédiatement toute utilisation de la plateforme.
              </p>
            </div>

            {/* 2. Vocation du Site */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <Globe className="w-5 h-5 text-church-blue-600" />
                2. Vocation Pastorale et Éditoriale
              </h2>
              <p>Le Site a pour vocation exclusive :</p>
              <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
                <li>
                  De diffuser l'Évangile de Jésus-Christ et les enseignements
                  bibliques conformément à la foi chrétienne évangélique.
                </li>
                <li>
                  D'informer les membres et le public des horaires des cultes,
                  des rassemblements spirituels et des activités communautaires
                  des campus de Montréal et de la Rive-Sud.
                </li>
                <li>
                  De mettre gratuitement à disposition le répertoire numérisé
                  des <em>Chants d'Espérance</em> à des fins de louange,
                  d'édification personnelle et de célébration liturgique.
                </li>
                <li>
                  De publier des articles de réflexion théologique, des études
                  doctrinales et des témoignages chrétiens originaux.
                </li>
              </ul>
            </div>

            {/* 3. Propriété Intellectuelle */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <BookOpen className="w-5 h-5 text-church-blue-600" />
                3. Propriété Intellectuelle et Droits d'Auteur
              </h2>
              <p>
                L'ensemble des éléments graphiques, photographies du sanctuaire,
                logos, enregistrements audio-vidéo, sermons rédigés et articles
                publiés sur le Site sont la propriété exclusive de l'Église de
                Dieu Garizim ou font l'objet d'une autorisation expresse de
                diffusion.
              </p>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs sm:text-sm space-y-2">
                <p className="font-semibold text-slate-900">
                  Utilisation des Chants d'Espérance et des Articles :
                </p>
                <p>
                  Les paroles des cantiques et les textes de nos articles peuvent
                  être consultés, téléchargés ou imprimés pour un usage
                  strictement personnel, familial ou liturgique non commercial.
                  Toute reproduction massive, réédition commerciale ou
                  rediffusion lucrative sans l'accord préalable écrit de la
                  direction pastorale est formellement interdite.
                </p>
              </div>
            </div>

            {/* 4. Utilisation Éthique */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                4. Conduite de l'Utilisateur
              </h2>
              <p>En naviguant sur notre plateforme, l'utilisateur s'engage à :</p>
              <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
                <li>
                  Ne pas entraver ou tenter d'entraver le fonctionnement normal
                  des serveurs et de l'infrastructure web.
                </li>
                <li>
                  Ne pas utiliser de robots, d'aspirateurs de données (scrapers)
                  ou de dispositifs automatiques sans autorisation technique
                  préalable.
                </li>
                <li>
                  Respecter l'esprit de courtoisie chrétienne et d'édification
                  dans toute communication transmise via nos formulaires de
                  contact ou nos canaux de prière.
                </li>
              </ul>
            </div>

            {/* 5. Liens Tiers et Dons */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <ShieldAlert className="w-5 h-5 text-church-blue-600" />
                5. Liens Hypertextes Externes & Services Tiers
              </h2>
              <p>
                Le Site peut contenir des liens menant vers des sites tiers
                (chaîne YouTube officielle, réseaux sociaux, plateformes de don
                comme PayPal). L'Église de Dieu Garizim n'exerce aucun contrôle
                éditorial sur le contenu ou les politiques de ces services
                externes et décline toute responsabilité quant à leur
                utilisation par l'internaute.
              </p>
            </div>

            {/* 6. Limitation de Responsabilité */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                6. Limitation de Responsabilité
              </h2>
              <p>
                L'Église s'efforce de maintenir les informations publiées
                (horaires de culte, programmation d'évènements, paroles de
                chants) aussi exactes et à jour que possible. Toutefois, des
                modifications exceptionnelles peuvent intervenir sans préavis
                (intempéries hivernales, ajustements de dernière minute du
                calendrier pastoral). L'Église ne saurait être tenue responsable
                d'éventuelles interruptions temporaires de service ou d'erreurs
                typographiques mineures.
              </p>
            </div>

            {/* 7. Droit Applicable */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                <Gavel className="w-5 h-5 text-church-blue-600" />
                7. Droit Applicable et Juridiction Compétente
              </h2>
              <p>
                Les présentes conditions générales d'utilisation sont régies et
                interprétées conformément aux lois en vigueur dans la province
                de Québec et aux lois fédérales du Canada qui s'y appliquent. En
                cas de litige relatif à l'interprétation ou à l'exécution des
                présentes, les tribunaux du district judiciaire de Montréal
                seront seuls compétents.
              </p>
            </div>

            {/* Contact */}
            <div className="p-6 bg-slate-900 text-white rounded-2xl space-y-3">
              <h3 className="font-bold text-lg text-amber-400">
                Des questions concernant nos conditions d'utilisation ?
              </h3>
              <p className="text-sm text-slate-300">
                Notre secrétariat administratif et pastoral se tient à votre
                disposition pour toute demande de renseignement ou demande
                d'autorisation de reproduction :
              </p>
              <div className="pt-2 text-xs sm:text-sm text-slate-200 space-y-1">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>11800 4e Avenue, Montréal, QC H1E 5Y2</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-400 shrink-0" />
                  <a
                    href="mailto:egarizim@gmail.com"
                    className="underline hover:text-amber-300"
                  >
                    egarizim@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Back links */}
            <div className="pt-4 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 text-xs sm:text-sm text-slate-500">
              <Link
                to="/"
                className="hover:text-church-blue-700 font-medium transition-colors"
              >
                &larr; Retour à l'accueil
              </Link>
              <Link
                to="/politique-de-confidentialite"
                className="hover:text-church-blue-700 font-medium transition-colors underline"
              >
                Consulter la Politique de Confidentialité &rarr;
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PageTermsOfService;
